import React, {
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { ReactComponent as IncomingVehicle } from 'src/Images/icons/common/incomingMapVehicle.svg';
import { ReactComponent as CurrentVehicle } from 'src/Images/icons/common/currentVehicle.svg';
import { ReactComponent as VehiclesDelayedForStationComponent } from 'src/Images/icons/common/vehiclesDelayedForStationComponent.svg';
import { ReactComponent as ZoomIn } from 'src/Images/icons/common/zoomIn.svg';
import { ReactComponent as ZoomOut } from 'src/Images/icons/common/zoomOut.svg';
import { ReactComponent as ResetZoom } from 'src/Images/icons/common/resetZoom.svg';
import StationMap from 'src/Images/icons/vehicle-tracking/StationMap.png';
import 'src/style/overallDashboard.css';

function PointerBlue({ color }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="#3062CD">
      <circle cx="5" cy="5" r="4" fill={color} />
    </svg>
  );
}

const getClientXY = (e) => {
  const evt =
    e.touches?.[0] ||
    e.changedTouches?.[0] ||
    e.nativeEvent?.touches?.[0] ||
    e.nativeEvent?.changedTouches?.[0] ||
    e;
  return {
    clientX: evt.clientX,
    clientY: evt.clientY,
  };
};

export function VehicleTrackingStationMap({ data = [] }) {
  const stops = useMemo(
    () =>
      (Array.isArray(data) ? data : []).map((stop) => ({
        station_id: stop.station_id,
        station_uuid: stop.station_uuid,
        name: stop.station,
        incoming: stop.incoming ?? 0,
        current: stop.current ?? 0,
        delayed: stop.delayed ?? 0,
        delayed_detail: stop.delayed_detail ?? null,
        incoming_detail: stop.incoming_detail ?? null,
        current_detail: stop.current_detail ?? null,
      })),
    [data],
  );

  const pathRef = useRef(null);
  const wrapperRef = useRef(null);
  const [stopPositions, setStopPositions] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const pinchRef = useRef({
    active: false,
    distance: 0,
  });

  const getPinchDistance = (touches) => {
    if (!touches || touches.length < 2) return 0;
    const [t1, t2] = touches;
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const [tooltip, setTooltip] = useState({
    visible: false,
    label: '',
    clientX: 0,
    clientY: 0,
  });

  const positionsEqual = useCallback((a = [], b = []) => {
    if (a.length !== b.length) return false;
    for (let index = 0; index < a.length; index += 1) {
      if (Math.round(a[index].x) !== Math.round(b[index].x)) return false;
      if (Math.round(a[index].y) !== Math.round(b[index].y)) return false;
    }
    return true;
  }, []);

  useLayoutEffect(() => {
    const path = pathRef.current;
    if (!path || stops.length === 0) {
      setStopPositions((prev) => (prev.length === 0 ? prev : []));
    } else {
      const totalLength = path.getTotalLength();
      if (typeof totalLength === 'number' && totalLength > 0) {
        const { length } = stops;
        const offsetPath = 100;
        const step = (totalLength - 2 * offsetPath) / Math.max(1, length - 1);
        const positions = [];
        for (let index = 0; index < length; index += 1) {
          const point = path.getPointAtLength(offsetPath + index * step);
          positions.push({ x: point.x, y: point.y });
        }
        setStopPositions((prev) =>
          positionsEqual(prev, positions) ? prev : positions,
        );
      }
    }
  }, [stops, positionsEqual]);

  const vehicleTrackingKey = (vehicleTrackingObj) =>
    vehicleTrackingObj?.vehicle_tracking_number ??
    vehicleTrackingObj?.vehicle_no ??
    vehicleTrackingObj?.vehicle_number ??
    vehicleTrackingObj?.number ??
    vehicleTrackingObj?.id ??
    JSON.stringify(vehicleTrackingObj);

  const alertBetween = useMemo(() => {
    const arr = [];
    const seen = new Set();
    for (let index = 0; index < stops.length - 1; index += 1) {
      const a = stops[index];
      const b = stops[index + 1];
      const vehicleTrackingA = Array.isArray(a.delayed_detail?.vehicle_tracking)
        ? a.delayed_detail.vehicle_tracking
        : [];
      const vehicleTrackingB = Array.isArray(b.delayed_detail?.vehicle_tracking)
        ? b.delayed_detail.vehicle_tracking
        : [];
      const map = new Map();
      [...vehicleTrackingA, ...vehicleTrackingB].forEach(
        (vehicleTrackingObj) => {
          const key = vehicleTrackingKey(vehicleTrackingObj);
          if (!seen.has(key) && !map.has(key)) {
            map.set(key, vehicleTrackingObj);
            seen.add(key);
          }
        },
      );
      const list = Array.from(map.values());
      if (list.length > 0) {
        arr.push({
          index,
          count: list.length,
          vehicleTrackingList: list,
        });
      }
    }
    return arr;
  }, [stops]);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.2, 1));

  const handleDragStart = (e) => {
    const touches = e.touches || e.nativeEvent?.touches;
    if (touches && touches.length === 2) {
      const distance = getPinchDistance(touches);
      pinchRef.current = { active: true, distance };
      dragging.current = false;
      e.preventDefault();
      return;
    }
    if (e.button !== undefined && e.button !== 0) return;
    const { clientX, clientY } = getClientXY(e);
    dragging.current = true;
    lastPos.current = { x: clientX, y: clientY };
    e.preventDefault();
  };

  const handleDragMove = (e) => {
    const touches = e.touches || e.nativeEvent?.touches;
    if (pinchRef.current.active && touches && touches.length === 2) {
      const newDistance = getPinchDistance(touches);
      if (newDistance > 0 && pinchRef.current.distance > 0) {
        const scaleChange = newDistance / pinchRef.current.distance;
        setZoom((prevZoom) => Math.max(1, Math.min(3, prevZoom * scaleChange)));
        pinchRef.current.distance = newDistance;
      }
      e.preventDefault();
      return;
    }
    if (!dragging.current) return;
    const { clientX, clientY } = getClientXY(e);
    const dx = clientX - lastPos.current.x;
    const dy = clientY - lastPos.current.y;
    setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    lastPos.current = { x: clientX, y: clientY };
    e.preventDefault();
  };

  const stopDragging = () => {
    dragging.current = false;
    pinchRef.current = { active: false, distance: 0 };
  };

  useEffect(() => {
    window.addEventListener('mouseup', stopDragging);
    window.addEventListener('touchend', stopDragging);
    window.addEventListener('touchcancel', stopDragging);
    return () => {
      window.removeEventListener('mouseup', stopDragging);
      window.removeEventListener('touchend', stopDragging);
      window.removeEventListener('touchcancel', stopDragging);
    };
  }, []);

  const incomingDyGap = 18;
  const currentDyGap = 20;
  const incomingStartDy = -30;
  const currentStartDy = -20;

  const vehicleTrackingShortLabel = (vehicleTrackingObj) => {
    if (!vehicleTrackingObj) return 'Unknown';
    if (
      typeof vehicleTrackingObj === 'string' ||
      typeof vehicleTrackingObj === 'number'
    )
      return `${vehicleTrackingObj}`;
    if (vehicleTrackingObj.vehicle_no)
      return `${vehicleTrackingObj.vehicle_no}`;
    if (vehicleTrackingObj.vehicle_number)
      return `${vehicleTrackingObj.vehicle_number}`;
    if (vehicleTrackingObj.number) return `${vehicleTrackingObj.number}`;
    if (vehicleTrackingObj.vehicle_tracking_number)
      return `${vehicleTrackingObj.vehicle_tracking_number}`;
    try {
      const s = JSON.stringify(vehicleTrackingObj);
      return s.length > 6 ? `${s.slice(-6)}` : s;
    } catch {
      return 'Unknown';
    }
  };

  const showTooltipFor = (e, vehicleTrackingObj) => {
    const { clientX, clientY } = getClientXY(e);
    const label = vehicleTrackingShortLabel(vehicleTrackingObj);
    setTooltip({
      visible: true,
      label,
      clientX,
      clientY,
    });
  };

  const moveTooltip = (e) => {
    const { clientX, clientY } = getClientXY(e);
    setTooltip((t) => (t.visible ? { ...t, clientX, clientY } : t));
  };

  const hideTooltip = () => {
    setTooltip({ visible: false, label: '', clientX: 0, clientY: 0 });
  };

  const renderIncomingGroup = (pos, idx) => {
    const stop = stops[idx];
    if (!stop) return null;
    const baseKey =
      stop.station_uuid ||
      stop.station_id ||
      `${Math.round(pos.x)}-${Math.round(pos.y)}`;
    const incomingVehicles = Array.isArray(
      stop.incoming_detail?.vehicle_tracking,
    )
      ? stop.incoming_detail.vehicle_tracking
      : [];
    const fallbackCount = stop.incoming ?? 0;
    const items =
      incomingVehicles.length > 0
        ? incomingVehicles
        : Array.from({ length: fallbackCount }, () => null);
    return (
      <g
        key={`incoming-stop-${baseKey}`}
        transform={`translate(${pos.x}, ${pos.y})`}
      >
        {items.map((vehicleTrackingObj, k) => {
          const keyId =
            (vehicleTrackingObj &&
              (vehicleTrackingObj.vehicle_tracking_number ||
                vehicleTrackingObj.vehicle_no ||
                vehicleTrackingObj.vehicle_number ||
                vehicleTrackingObj.number)) ||
            `${baseKey}-inc-${k}`;
          const dy = incomingStartDy - items.length * 8 + k * incomingDyGap;
          const vehicleTrackingNumber =
            vehicleTrackingObj && vehicleTrackingObj.vehicle_tracking_number
              ? vehicleTrackingObj.vehicle_tracking_number
              : '';
          const goToDetail = (e) => {
            e.stopPropagation();
            if (vehicleTrackingNumber) {
              const url = `/${stop.station_id}/${vehicleTrackingNumber}`;
              window.open(url, '_blank');
            }
          };
          const handleTooltipStart = (e) => {
            e.stopPropagation();
            showTooltipFor(e, vehicleTrackingObj);
          };
          return (
            <g key={`incoming-${keyId}`} transform={`translate(${-70}, ${dy})`}>
              <g
                transform="translate(-8, -8) scale(0.95)"
                style={{
                  cursor: vehicleTrackingNumber ? 'pointer' : 'default',
                }}
                onClick={goToDetail}
                onMouseEnter={(e) => showTooltipFor(e, vehicleTrackingObj)}
                onMouseMove={moveTooltip}
                onMouseLeave={hideTooltip}
                onTouchStart={handleTooltipStart}
                onTouchMove={moveTooltip}
                onTouchEnd={hideTooltip}
                onTouchCancel={hideTooltip}
              >
                <IncomingVehicle />
              </g>
            </g>
          );
        })}
      </g>
    );
  };

  const renderCurrentGroup = (pos, idx) => {
    const stop = stops[idx];
    if (!stop) return null;
    const baseKey =
      stop.station_uuid ||
      stop.station_id ||
      `${Math.round(pos.x)}-${Math.round(pos.y)}`;
    const currentVehicles = Array.isArray(stop.current_detail?.vehicle_tracking)
      ? stop.current_detail.vehicle_tracking
      : [];
    const fallbackCount = stop.current ?? 0;
    const items =
      currentVehicles.length > 0
        ? currentVehicles
        : Array.from({ length: fallbackCount }, () => null);
    const hasCurrent = items.length > 0;
    const name = stop.name || '';
    return (
      <g
        key={`current-stop-${baseKey}`}
        transform={`translate(${pos.x}, ${pos.y})`}
      >
        <g>
          {hasCurrent ? (
            <>
              {items.map((vehicleTrackingObj, k) => {
                const keyId =
                  (vehicleTrackingObj &&
                    (vehicleTrackingObj.vehicle_tracking_number ||
                      vehicleTrackingObj.vehicle_no ||
                      vehicleTrackingObj.vehicle_number ||
                      vehicleTrackingObj.number)) ||
                  `${baseKey}-cur-${k}`;
                const dy = currentStartDy - items.length * 8 + k * currentDyGap;
                const vehicleTrackingNumber =
                  vehicleTrackingObj &&
                  vehicleTrackingObj.vehicle_tracking_number
                    ? vehicleTrackingObj.vehicle_tracking_number
                    : '';
                const goToDetail = (e) => {
                  e.stopPropagation();
                  if (vehicleTrackingNumber) {
                    const url = `/${stop.station_id}/${vehicleTrackingNumber}`;
                    window.open(url, '_blank');
                  }
                };
                const handleTooltipStart = (e) => {
                  e.stopPropagation();
                  showTooltipFor(e, vehicleTrackingObj);
                };
                return (
                  <g
                    key={`current-${keyId}`}
                    transform={`translate(${10}, ${dy})`}
                  >
                    <g
                      transform="translate(-8, -8) scale(0.95)"
                      style={{
                        cursor: vehicleTrackingNumber ? 'pointer' : 'default',
                      }}
                      onClick={goToDetail}
                      onMouseEnter={(e) =>
                        showTooltipFor(e, vehicleTrackingObj)
                      }
                      onMouseMove={moveTooltip}
                      onMouseLeave={hideTooltip}
                      onTouchStart={handleTooltipStart}
                      onTouchMove={moveTooltip}
                      onTouchEnd={hideTooltip}
                      onTouchCancel={hideTooltip}
                    >
                      <CurrentVehicle />
                    </g>
                  </g>
                );
              })}
              <g transform="translate(20, -30)">
                <PointerBlue color="#3062CD" />
              </g>
            </>
          ) : (
            <g transform="translate(-10, -5)">
              <PointerBlue color="#0E9A26" />
            </g>
          )}
          <text
            fontSize="12"
            fill="#9f9f9f"
            fontWeight="bold"
            textAnchor="middle"
            alignmentBaseline="middle"
            lengthAdjust="spacingAndGlyphs"
            transform={
              hasCurrent
                ? `rotate(-55, -36, -${75 + name.length})`
                : `rotate(-55, -38, -${45 + name.length})`
            }
          >
            {name}
          </text>
        </g>
      </g>
    );
  };

  const renderDelayedAlert = (alert) => {
    const { index, vehicleTrackingList } = alert;
    if (
      stopPositions.length <= index + 1 ||
      !stopPositions[index] ||
      !stopPositions[index + 1]
    )
      return null;
    const midX = (stopPositions[index].x + stopPositions[index + 1].x) / 2;
    const midY = (stopPositions[index].y + stopPositions[index + 1].y) / 2;
    const circleCX = midX - 100;
    const circleCY = midY + 175;
    const circleR = 25;
    const showVehicles = Array.isArray(vehicleTrackingList)
      ? vehicleTrackingList
      : [];
    return (
      <g key={`alert-${index}`}>
        <line
          x1={midX}
          y1={midY}
          x2={midX}
          y2={midY + 200}
          stroke="#9f9f9f"
          strokeDasharray="4 4"
          strokeWidth="1"
          transform={`rotate(30, ${midX}, ${midY})`}
        />
        <circle
          cx={circleCX}
          cy={circleCY}
          r={25}
          fill="#e7332f0d"
          stroke="#FF3232"
          strokeWidth="0.5"
        />
        <circle
          cx={circleCX}
          cy={circleCY}
          r={40}
          fill="#e7332f1a"
          stroke="#FF3232"
          strokeWidth="0.5"
        />
        {showVehicles.map((vehicleTrackingObj, vi) => {
          const vehicleTrackingNumber =
            (vehicleTrackingObj &&
              (vehicleTrackingObj.vehicle_tracking_number ||
                vehicleTrackingObj.vehicle_no ||
                vehicleTrackingObj.vehicle_number ||
                vehicleTrackingObj.number)) ||
            '';
          const keyId =
            vehicleTrackingNumber ||
            JSON.stringify(vehicleTrackingObj) ||
            `veh-${vi}`;
          const { length } = showVehicles;
          const angle = length === 1 ? 0 : (vi / length) * Math.PI * 2;
          const iconRadius = circleR - 6;
          const iconX = circleCX + Math.cos(angle) * iconRadius;
          const iconY = circleCY + Math.sin(angle) * iconRadius;
          const goToDetail = (e) => {
            e.stopPropagation();
            const stationId = stops[index]?.station_id;
            if (vehicleTrackingNumber && stationId) {
              const url = `/${stationId}/${vehicleTrackingNumber}`;
              window.open(url, '_blank');
            }
          };
          const handleTooltipStart = (e) => {
            e.stopPropagation();
            showTooltipFor(e, vehicleTrackingObj);
          };
          return (
            <g
              key={`alert-${index}-veh-${keyId}`}
              transform={`translate(${iconX}, ${iconY})`}
              style={{ cursor: vehicleTrackingNumber ? 'pointer' : 'default' }}
              onClick={goToDetail}
              onMouseEnter={(e) => showTooltipFor(e, vehicleTrackingObj)}
              onMouseMove={moveTooltip}
              onMouseLeave={hideTooltip}
              onTouchStart={handleTooltipStart}
              onTouchMove={moveTooltip}
              onTouchEnd={hideTooltip}
              onTouchCancel={hideTooltip}
            >
              <g transform="translate(-22, -20) scale(0.95)">
                <VehiclesDelayedForStationComponent />
              </g>
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <div
      ref={wrapperRef}
      className="station-map-wrapper"
      style={{
        userSelect: dragging.current ? 'none' : 'auto',
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <div className="zoom-container div-flex-row cg-10">
        <button
          type="button"
          onClick={zoomIn}
          aria-label="Zoom in"
          className="create-btn-outlined"
        >
          <ZoomIn className="icon-size-sm" />
        </button>
        <button
          type="button"
          onClick={zoomOut}
          aria-label="Zoom out"
          className="create-btn-outlined"
        >
          <ZoomOut className="icon-size-sm" />
        </button>
        <button
          type="button"
          onClick={() => {
            setZoom(1);
            setOffset({ x: 0, y: 0 });
          }}
          aria-label="Reset zoom"
          className="create-btn-outlined"
        >
          <ResetZoom className="icon-size-sm" />
        </button>
      </div>

      <div
        role="button"
        tabIndex={0}
        className={`station-map-canvas${dragging.current ? ' dragging' : ''}`}
        style={{
          backgroundImage: `url(${StationMap})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          width: '100%',
          maxWidth: '100%',
        }}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={stopDragging}
        onMouseLeave={stopDragging}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={stopDragging}
        onTouchCancel={stopDragging}
      >
        <svg
          viewBox="0 0 1200 140"
          width="100%"
          height="100%"
          preserveAspectRatio="xMidYMid meet"
          className="station-map-svg"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            touchAction: 'none',
          }}
        >
          <path
            ref={pathRef}
            d="M 0,-100 C 250,80 900,-20 1200,200"
            fill="none"
            stroke="#3962d4b3"
            strokeWidth="4"
            strokeLinecap="round"
          />
          {stopPositions.map((pos, idx) => renderIncomingGroup(pos, idx))}
          {stopPositions.map((pos, idx) => renderCurrentGroup(pos, idx))}
          {alertBetween.map((a) => renderDelayedAlert(a))}
        </svg>

        {tooltip.visible &&
          wrapperRef.current &&
          (() => {
            const rect = wrapperRef.current.getBoundingClientRect();
            const left = tooltip.clientX - rect.left + 10;
            const top = tooltip.clientY - rect.top + 10;
            return (
              <div
                className="vehicle-tooltip"
                style={{
                  left,
                  top,
                }}
              >
                {tooltip.label}
              </div>
            );
          })()}
      </div>
    </div>
  );
}

export default VehicleTrackingStationMap;
